<template>
  <div ref="tagMenu" class="tag-menu">
    <div class="menu-header d-flex">
      <sera-input
        placeholder="Create or Select Tag"
        class="mt-4 mb-0 mr-2 d-flex w-100"
        @change="filterTagsByInput"
      />
      <button
        aria-label="Close"
        class="close-button d-flex justify-content-center align-items-center p-1 link"
        @click="closeMenu"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div>
      <div v-if="filteredTags.length > 0">
        <span class="menu-label">{{ menuHeader }}</span>
        <TagList
          :tags="filteredTags ?? []"
          :click-emit="'add-tag'"
          @add-tag="emitAddTag"
        />
      </div>
      <div v-else-if="filterText.trim()" class="create-tag-prompt">
        <SeraButton
          icon="fa fa-plus fa-fw"
          text="Create New Tag"
          outline
          @click="handleAddNewClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import TagList from "@/Tags/TagList.vue";
import { Tag } from "@/typings/tag";

const store = useStore();
const props = defineProps({
  taggableType: {
    type: String,
    required: true
  },
  tagCount: {
    type: Number,
    default: 0
  }
});

// Emit event for adding tags
const emit = defineEmits(["add-tag", "close-menu", "open-tag-modal"]);

watch(
  () => props.tagCount,
  () => {
    if (props.tagCount >= 5) {
      resetMenu();
      closeMenu();
    }
  }
);

const tags = ref<Tag[]>([]);
const tagMenu = ref<HTMLElement | null>(null);
const filterText = ref("");
const menuHeader = computed(() => {
  return `${props.taggableType.replace(/\b\w/g, (char) =>
    char.toUpperCase()
  )} Tags`;
});

const handleClickOutside = (event: MouseEvent) => {
  if (tagMenu.value && !tagMenu.value.contains(event.target as Node)) {
    closeMenu();
  }
};

// Fetch available tags from API
const fetchTags = async () => {
  const response = await store.dispatch("fetchTags", props.taggableType);
  tags.value = Array.isArray(response.data.tags) ? response.data.tags : [];
};

const filterTagsByInput = (input) => {
  filterText.value = input.detail;
};

const filteredTags = computed(() => {
  // If filterText is empty, return all tags
  if (!filterText.value.trim()) {
    return tags.value;
  }
  // Otherwise, filter tags based on the input
  return tags.value.filter((tag) =>
    tag.label.toLowerCase().includes(filterText.value.toLowerCase())
  );
});

// Emit Methods
const emitAddTag = (tag) => {
  emit("add-tag", tag);
};

const closeMenu = () => {
  emit("close-menu");
};

const resetMenu = (filter = "") => {
  filterText.value = filter;
};

const handleAddNewClick = () => {
  emit("open-tag-modal", filterText.value);
  closeMenu();
};

// Fetch tags on component mount
onMounted(() => {
  fetchTags();
  document.addEventListener("mousedown", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
});
</script>

<style scoped>
.tag-menu {
  position: absolute;
  top: 50px;
  right: 10px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 10px;
  z-index: 1000;
  width: 250px;
  display: flex;
  flex-direction: column;
}

.close-button {
  all: unset;
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  font-size: 14px;
  cursor: pointer;
}

.menu-header {
  margin-bottom: 10px;
}

.menu-label {
  font-weight: bold;
  margin-bottom: 8px;
  display: block;
}

.create-tag-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
  text-align: center;
}
</style>
